import { Download, Play, RefreshCw, Server, Square } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useVelociraptorSettings } from '../../hooks'
import { DEPLOY_EXTERNAL } from './constants'

type ServiceControl = ReturnType<typeof useVelociraptorSettings>['serviceControl']

interface ServiceControlPanelProps {
  isRunning: boolean
  serviceControl: ServiceControl
  onRefresh: () => Promise<void>
  deploymentType: string
}

interface ServiceErrorBannerProps {
  error: string | null
}

function ServiceErrorBanner({ error }: ServiceErrorBannerProps): React.ReactElement | null {
  if (!error) return null
  return (
    <div className="mt-2 w-full rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
      <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
    </div>
  )
}

function ExternalServicePanel({
  serviceControl,
}: {
  serviceControl: ServiceControl
}): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex flex-wrap gap-3">
      <div className="flex w-full items-center gap-2 rounded-lg border border-[#4e5e82]/20 bg-[#4e5e82]/10 px-4 py-3 dark:border-[#8a9bba]/20 dark:bg-[#8a9bba]/10">
        <Server className="h-4 w-4 shrink-0 text-[#5c6e94] dark:text-[#8a9bba]" />
        <p className="text-sm text-[#5c6e94] dark:text-[#8a9bba]">
          {t('velociraptorSettings.externalServiceBanner')}
        </p>
      </div>
      <ServiceErrorBanner error={serviceControl.error} />
    </div>
  )
}

interface RunControlButtonProps {
  onClick: () => void
  loading: boolean
  loadingIconClass?: string
}

function StopButton({ onClick, loading }: RunControlButtonProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
    >
      {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
      {t('velociraptorSettings.stopService')}
    </button>
  )
}

function StartButton({ onClick, loading }: RunControlButtonProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
    >
      {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
      {t('velociraptorSettings.startService')}
    </button>
  )
}

interface UpgradeButtonProps {
  onClick: () => void
  loading: boolean
  disabled: boolean
}

function UpgradeButton({ onClick, loading, disabled }: UpgradeButtonProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-700"
    >
      {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      {t('velociraptorSettings.upgrade')}
    </button>
  )
}

export function ServiceControlPanel({
  isRunning,
  serviceControl,
  onRefresh,
  deploymentType,
}: ServiceControlPanelProps): React.ReactElement {
  const isExternal = deploymentType === DEPLOY_EXTERNAL

  const handleAction = async (
    action: () => Promise<{ success: boolean } | null | undefined>
  ): Promise<void> => {
    const result = await action()
    if (result?.success) {
      await onRefresh()
    }
  }

  if (isExternal) {
    return <ExternalServicePanel serviceControl={serviceControl} />
  }

  return (
    <div className="flex flex-wrap gap-3">
      {isRunning ? (
        <StopButton
          onClick={() => {
            void handleAction(serviceControl.stop)
          }}
          loading={serviceControl.stopping}
        />
      ) : (
        <StartButton
          onClick={() => {
            void handleAction(serviceControl.start)
          }}
          loading={serviceControl.starting}
        />
      )}
      <UpgradeButton
        onClick={() => {
          void handleAction(serviceControl.upgrade)
        }}
        loading={serviceControl.upgrading}
        disabled={serviceControl.upgrading || !isRunning}
      />
      <ServiceErrorBanner error={serviceControl.error} />
    </div>
  )
}
